import React from "react";
import getAvatarUrl from "../../../utils/get-avatar-url";
import {
  formatBookingSlotLabel,
  ptBookingMatchesOccurrence,
} from "./pt-session-slots";

// ─── Utilities ────────────────────────────────────────────────────────────────

function formatPanelDate(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "—";
  const day = date.getDate();
  const month = date.toLocaleDateString("en-US", { month: "short" });
  const year = date.getFullYear();
  const weekday = date.toLocaleDateString("en-US", { weekday: "short" });
  return `${month} ${day}, ${year} (${weekday})`;
}

function formatPanelTimeRange(start, end) {
  if (!(start instanceof Date)) return "—";
  const opts = { hour: "numeric", minute: "2-digit", hour12: true };
  const a = start
    .toLocaleTimeString("en-US", opts)
    .toLowerCase()
    .replace(" ", "");
  if (!(end instanceof Date) || Number.isNaN(end.getTime())) return a;
  const b = end
    .toLocaleTimeString("en-US", opts)
    .toLowerCase()
    .replace(" ", "");
  return `${a} - ${b}`;
}

function formatDurationMins(minutes) {
  if (minutes == null || Number.isNaN(Number(minutes))) return "—";
  const m = Number(minutes);
  if (m < 60) return `${m} mins`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r ? `${h}h ${r}m` : `${h}h`;
}

function normalizeStatus(raw) {
  const s = String(raw ?? "").toLowerCase();
  if (s === "attended") return "attended";
  if (s === "absent" || s === "no_show") return "absent";
  return "pending";
}

function resolveAvatarUrl(raw, theme) {
  if (!raw) return getAvatarUrl({}, false, theme, theme !== "dark");
  if (typeof raw === "string") return raw;
  if (typeof raw === "object" && typeof raw.s3Path === "string" && raw.s3Path) {
    return getAvatarUrl({ avatar: raw }, false, theme, theme !== "dark");
  }
  return getAvatarUrl({}, false, theme, theme !== "dark");
}

function parseParticipants(list) {
  return list.map((b, i) => ({
    _key: i,
    dbId: String(b._id ?? b.id ?? b.booking_number ?? i),
    name: String(b.member_name ?? "Member"),
    email: b.member_email ? String(b.member_email) : null,
    avatarRaw: b.member_avatar ?? null,
    status: normalizeStatus(b.status),
    startAt: b.start_datetime ?? null,
    bookingTimezone: b.event_timezone ?? null,
  }));
}

function extractCoaches(raw, theme) {
  if (!raw) return [];
  const list = Array.isArray(raw.coaches) ? raw.coaches : [];
  const seen = new Set();
  const out = [];
  for (const entry of list) {
    const c = entry?.coachId ?? entry?.coach ?? entry;
    if (!c || typeof c !== "object") continue;
    const id = String(c._id ?? c.id ?? "");
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push({
      id,
      name: String(c.name ?? "").trim() || "Coach",
      role: String(c.role ?? "").trim() || "Coach",
      avatarUrl: getAvatarUrl(c, false, theme, theme !== "dark"),
    });
  }
  return out;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function StatusButtons({ status, onChange }) {
  return (
    <div className="mark-attendance-status-btns">
      <button
        type="button"
        className={`mark-attendance-status-btn mark-attendance-status-btn--attended${status === "attended" ? " mark-attendance-status-btn--active" : ""}`}
        onClick={() => onChange("attended")}
      >
        {status === "attended" && (
          <img src="/img/svg/vuesax-linear-tick-square-scheduling.svg" alt="" />
        )}
        Attended
      </button>
      <button
        type="button"
        className={`mark-attendance-status-btn mark-attendance-status-btn--absent${status === "absent" ? " mark-attendance-status-btn--active" : ""}`}
        onClick={() => onChange("absent")}
      >
        {status === "absent" && (
          <img
            src="/img/svg/vuesax-linear-tick-square-scheduling-red.svg"
            alt=""
          />
        )}
        Absent
      </button>
      <button
        type="button"
        className={`mark-attendance-status-btn mark-attendance-status-btn--pending${status === "pending" ? " mark-attendance-status-btn--active" : ""}`}
        onClick={() => onChange("pending")}
      >
        {status === "pending" && (
          <img
            src="/img/svg/vuesax-linear-tick-square-scheduling-yellow.svg"
            alt=""
          />
        )}
        Pending
      </button>
    </div>
  );
}

function DetailRow({ icon, label, children }) {
  return (
    <div className="mark-attendance-detail-row">
      <span className="mark-attendance-detail-label">
        <img src={icon} alt="" aria-hidden />
        {label}
      </span>
      <span className="mark-attendance-detail-value">{children}</span>
    </div>
  );
}

function CoachCluster({ coaches }) {
  const shown = coaches.slice(0, 3);
  const count = shown.length || 1;
  const thumbSize = count === 1 ? 72 : 42;
  return (
    <div
      className="pt-attendance-header-thumb scheduling-history-pt-thumb"
      data-count={count}
    >
      <div className="scheduling-history-pt-thumb-cluster">
        {shown.length > 0 ? (
          shown.map((c, i) => (
            <span
              key={i}
              className="scheduling-history-avatar"
              style={{
                width: thumbSize,
                height: thumbSize,
                fontSize: Math.floor(thumbSize * 0.38),
              }}
              title={c.name}
            >
              {c.avatarUrl ? (
                <img
                  src={c.avatarUrl}
                  alt={c.name}
                  className="scheduling-history-avatar-img"
                  style={{ width: thumbSize, height: thumbSize }}
                />
              ) : c.name ? (
                c.name.charAt(0).toUpperCase()
              ) : (
                "?"
              )}
            </span>
          ))
        ) : (
          <span
            className="scheduling-history-avatar"
            style={{ width: thumbSize, height: thumbSize }}
          >
            ?
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

const MarkAttendancePtView = ({ event, onBack, onEdit, theme = "dark" }) => {
  const [participants, setParticipants] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [eventDetail, setEventDetail] = React.useState(null);

  const rawId = event?.rawId;

  React.useEffect(() => {
    if (!rawId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({ bookable_event_id: rawId });
    if (event.start instanceof Date && !Number.isNaN(event.start.getTime())) {
      params.set("date", event.start.toISOString().slice(0, 10));
    }
    fetch(`/actions/scheduling/coach/bookings?${params}`, {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    })
      .then(async (res) => {
        const data = await res.json().catch(() => []);
        return Array.isArray(data) ? data : [];
      })
      .then((list) => {
        if (cancelled) return;
        const matching = list.filter((booking) =>
          ptBookingMatchesOccurrence(
            booking,
            { start: event.start, end: event.end },
            rawId,
          ),
        );
        setParticipants(parseParticipants(matching));
      })
      .catch(() => {
        if (cancelled) return;
        setError("Could not load participants.");
        setParticipants([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [rawId, event?.start]);

  React.useEffect(() => {
    if (!rawId) return;
    let cancelled = false;
    setEventDetail(null);
    fetch("/actions/scheduling/pt-sessions", {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    })
      .then((r) => r.json().catch(() => []))
      .then((list) => {
        if (cancelled) return;
        const arr = Array.isArray(list) ? list : [];
        const found = arr.find(
          (item) => String(item._id ?? item.id) === String(rawId)
        );
        if (found) setEventDetail(found);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [rawId]);

  const headerCoaches = React.useMemo(() => {
    if (Array.isArray(event.coaches) && event.coaches.length > 0)
      return event.coaches;
    return extractCoaches(eventDetail ?? event.raw, theme);
  }, [event.coaches, eventDetail, event.raw, theme]);

  const durationLabel =
    event.duration ??
    formatDurationMins(
      eventDetail?.duration_minutes ?? event.raw?.duration_minutes
    );

  const ratioLabel = React.useMemo(() => {
    if (event.ratio) return event.ratio;
    const cap = eventDetail?.capacity ?? event.raw?.capacity;
    if (cap == null) return null;
    return `${participants.length}:${cap}`;
  }, [event.ratio, eventDetail, event.raw, participants.length]);

  const location =
    eventDetail?.location?.toString().trim() ||
    event.location ||
    event.raw?.location ||
    "—";
  const durationMins =
    eventDetail?.duration_minutes ?? event.raw?.duration_minutes ?? null;
  const capacity = eventDetail?.capacity ?? event.raw?.capacity ?? null;
  const sessionTimezone = eventDetail?.timezone ?? event.raw?.timezone ?? null;

  const participantRows = React.useMemo(
    () =>
      participants.map((p) => ({
        ...p,
        avatarUrl: resolveAvatarUrl(p.avatarRaw, theme),
        slotLabel: formatBookingSlotLabel(
          p.startAt,
          p.bookingTimezone || sessionTimezone
        ),
      })),
    [participants, sessionTimezone, theme]
  );

  function setParticipantStatus(key, status) {
    setParticipants((prev) => {
      if (status === "attended" || status === "absent") {
        const p = prev.find((m) => m._key === key);
        if (p) {
          fetch(
            `/actions/scheduling/bookings/${encodeURIComponent(p.dbId)}/attendance`,
            {
              method: "POST",
              credentials: "same-origin",
              headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
              },
              body: JSON.stringify({ status }),
            }
          ).catch(() => {});
        }
      }
      return prev.map((m) => (m._key === key ? { ...m, status } : m));
    });
  }

  return (
    <div className="mark-attendance-view">
      <button type="button" className="mark-attendance-back" onClick={onBack}>
        <span className="mark-attendance-back-icon">
          <img
            src="/img/new-icons/arrow-square-left.svg"
            alt=""
            className="mark-attendance-back-arrow"
          />
        </span>
        Back
      </button>

      <div className="pt-attendance-header">
        <CoachCluster coaches={headerCoaches} />
        <div className="pt-attendance-header-info">
          <h1 className="pt-attendance-header-title">
            {event?.title ?? "PT Session"}
          </h1>
          <p className="pt-attendance-header-sub">
            {durationLabel}
            {ratioLabel && <> &bull; {ratioLabel}</>}
          </p>
          <button
            type="button"
            className="mark-attendance-edit-btn"
            onClick={() => onEdit?.(event)}
          >
            <img src="/img/new-icons/edit-scheduling.svg" alt="" />
            Edit PT Session
          </button>
        </div>
      </div>

      <div className="mark-attendance-card">
        <div className="mark-attendance-details">
          <DetailRow icon="/img/new-icons/type-scheduling.svg" label="Type">
            <span className="mark-attendance-detail-type mark-attendance-detail-type--pt">
              PT Session
            </span>
          </DetailRow>

          <div className="mark-attendance-detail-row mark-attendance-detail-row--participants">
            <span className="mark-attendance-detail-label">
              <img
                src="/img/new-icons/vuesax-linear-user.svg"
                alt=""
                aria-hidden
              />
              Participant
            </span>
            <div className="pt-attendance-participants">
              {loading && (
                <p
                  className="mark-attendance-loading"
                  style={{ padding: "0.5rem 0", textAlign: "left" }}
                >
                  Loading…
                </p>
              )}
              {!loading && error && (
                <p
                  className="mark-attendance-error"
                  style={{ padding: "0.5rem 0", textAlign: "left" }}
                >
                  {error}
                </p>
              )}
              {!loading && !error && participants.length === 0 && (
                <p
                  className="mark-attendance-empty"
                  style={{ padding: "0.5rem 0", textAlign: "left" }}
                >
                  No participants found.
                </p>
              )}
              {!loading &&
                !error &&
                participantRows.map((p) => (
                  <div key={p._key} className="pt-attendance-participant">
                    <div className="pt-attendance-participant-info">
                      {p.avatarUrl ? (
                        <img
                          src={p.avatarUrl}
                          alt={p.name}
                          className="mark-attendance-avatar-img"
                        />
                      ) : (
                        <span className="mark-attendance-avatar">
                          {p.name ? p.name.charAt(0).toUpperCase() : "?"}
                        </span>
                      )}
                      <div>
                        <div className="mark-attendance-member-name">
                          {p.name}
                        </div>
                        {p.email && (
                          <div className="mark-attendance-member-email">
                            {p.email}
                          </div>
                        )}
                        {p.slotLabel && (
                          <div className="pt-attendance-participant-slot">
                            {p.slotLabel}
                          </div>
                        )}
                      </div>
                    </div>
                    <StatusButtons
                      status={p.status}
                      onChange={(s) => setParticipantStatus(p._key, s)}
                    />
                  </div>
                ))}
            </div>
          </div>

          <DetailRow
            icon="/img/new-icons/vuesax-linear-calendar-schedule.svg"
            label="Date"
          >
            {formatPanelDate(event?.start)}
          </DetailRow>
          <DetailRow icon="/img/new-icons/vuesax-linear-clock.svg" label="Time">
            {formatPanelTimeRange(event?.start, event?.end)}
          </DetailRow>
          <DetailRow
            icon="/img/new-icons/duration-scheduling.svg"
            label="Duration"
          >
            {formatDurationMins(durationMins)}
          </DetailRow>
          {capacity != null && (
            <DetailRow
              icon="/img/new-icons/vuesax-linear-user.svg"
              label="Capacity"
            >
              {ratioLabel ?? `${capacity}`}
            </DetailRow>
          )}
          <DetailRow
            icon="/img/new-icons/location-scheduling.svg"
            label="Location"
          >
            {location}
          </DetailRow>

          {headerCoaches.length > 0 && (
            <div className="mark-attendance-detail-row mark-attendance-detail-row--coaches">
              <span className="mark-attendance-detail-label">
                <img
                  src="/img/new-icons/coach-scheduling.svg"
                  alt=""
                  aria-hidden
                />
                Coaches
              </span>
              <ul className="mark-attendance-detail-coaches">
                {headerCoaches.map((c) => (
                  <li key={c.id} className="mark-attendance-detail-coach">
                    <img
                      src={c.avatarUrl}
                      alt=""
                      className="mark-attendance-detail-coach-avatar"
                    />
                    <span className="mark-attendance-detail-coach-text">
                      <span className="mark-attendance-detail-coach-name">
                        {c.name}
                      </span>
                      <span className="mark-attendance-detail-coach-role">
                        {c.role}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MarkAttendancePtView;
